// @generated-imports
import { dom, state } from './core.js';
import { LOCAL_AMBIGUOUS_EXTENSIONS, LOCAL_CORE_LABELS, LOCAL_EXTENSION_CORE_MAP, getFileExtension } from './dragdrop.js';
import { gpPickerHighlight } from './gamepad.js';
import { layerClosed, layerOpened } from './layers.js';
// @generated-imports
/* ===========================================================================
   DETECÇÃO DE CORE DENTRO DE .ZIP (sem descompactar)
   ---------------------------------------------------------------------------
   O formato ZIP guarda a lista de arquivos no "central directory", no FINAL
   do arquivo. Basta ler os últimos KB para descobrir os nomes internos —
   sem inflar nada e sem dependência externa. Com as extensões internas:
     • extensão inequívoca (.nes, .sfc, .gba…) → core direto;
     • dump de CD (.cue/.bin/.iso/.chd)        → seletor só com sistemas de CD;
     • muitos arquivos irreconhecíveis          → provável romset arcade.
   .7z fica de fora (formato bem mais complexo); segue no fluxo padrão.
   ======================================================================== */

// Arquivos que não dizem nada sobre o sistema (manuais, capas, metadados).
// ATENÇÃO: '.md' NÃO entra aqui — é a extensão clássica de Mega Drive.
// Markdown é tratado à parte, pelo nome do arquivo (README.md etc.).
const ZIP_META_EXTENSIONS = new Set([
  'txt', 'nfo', 'diz', 'pdf', 'doc', 'rtf',
  'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'ico',
  'html', 'htm', 'xml', 'ini', 'cfg', 'log', 'json', 'db',
  'sav', 'srm', 'state',
]);

// Nomes típicos de documentação: só para desempatar o caso "arquivo.md",
// que tanto pode ser uma ROM de Mega Drive quanto um Markdown de projeto.
const DOCUMENTATION_BASENAME = /^(read\.?me|readme|changelog|license|licen[çc]a|leia-?me|contributing|authors?|credits|notes?|todo|install)$/i;

function isDocumentationMarkdown(name) {
  const base = name.split('/').pop();
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return false;
  const ext = base.slice(dot + 1).toLowerCase();
  if (ext !== 'md') return false;
  return DOCUMENTATION_BASENAME.test(base.slice(0, dot));
}

/**
 * Lê os nomes de arquivo do central directory de um zip.
 * Retorna array de nomes ou null se o zip não puder ser interpretado
 * (assinatura ausente, zip64, arquivo corrompido…).
 */
async function listZipEntries(file) {
  try {
    // EOCD (End Of Central Directory) fica nos últimos 22 bytes + até 64KB
    // de comentário. Ler esse rabo cobre qualquer zip válido.
    const tailSize = Math.min(file.size, 65558);
    if (tailSize < 22) return null;

    const tail = new DataView(await file.slice(file.size - tailSize).arrayBuffer());

    // Procura a assinatura EOCD (0x06054b50) de trás para frente.
    let eocd = -1;
    for (let i = tail.byteLength - 22; i >= 0; i -= 1) {
      if (tail.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd === -1) return null;

    const cdSize = tail.getUint32(eocd + 12, true);
    const cdOffset = tail.getUint32(eocd + 16, true);
    // 0xFFFFFFFF sinaliza zip64 (arquivos > 4GB) — fora do escopo aqui.
    if (cdOffset === 0xffffffff || cdSize === 0xffffffff) return null;
    if (cdOffset + cdSize > file.size) return null;

    const cd = new DataView(await file.slice(cdOffset, cdOffset + cdSize).arrayBuffer());
    const bytes = new Uint8Array(cd.buffer);
    const decoder = new TextDecoder('utf-8');
    const names = [];

    let pos = 0;
    while (pos + 46 <= cd.byteLength) {
      if (cd.getUint32(pos, true) !== 0x02014b50) break; // fim das entradas
      const nameLen = cd.getUint16(pos + 28, true);
      const extraLen = cd.getUint16(pos + 30, true);
      const commentLen = cd.getUint16(pos + 32, true);
      names.push(decoder.decode(bytes.subarray(pos + 46, pos + 46 + nameLen)));
      pos += 46 + nameLen + extraLen + commentLen;
    }

    return names;
  } catch (_) {
    return null;
  }
}

/**
 * Decide o core a partir do conteúdo interno do zip.
 * Retorna:
 *   { core, reason }               → escolha automática;
 *   { candidates, message }        → seletor com lista restrita;
 *   null                           → nada conclusivo, segue o fluxo padrão.
 */
async function analyzeZipRom(file) {
  const names = await listZipEntries(file);
  if (!names || !names.length) return null;

  // Descarta pastas e lixo de sistema operacional.
  const entries = names.filter((name) => (
    name
    && !name.endsWith('/')
    && !name.startsWith('__MACOSX')
    && !name.split('/').pop().startsWith('.')
  ));
  if (!entries.length) return null;

  const extensions = new Set();
  entries.forEach((name) => {
    if (isDocumentationMarkdown(name)) return; // README.md e afins não contam
    const ext = getFileExtension(name);
    if (ext && !ZIP_META_EXTENSIONS.has(ext) && ext !== 'zip' && ext !== '7z') {
      extensions.add(ext);
    }
  });

  // Só metadados/documentação lá dentro: nada conclusivo → fluxo padrão.
  if (extensions.size === 0) return null;

  // Dump de CD dentro do zip: .cue manda (referencia as faixas .bin).
  // Sem .cue, mas com imagem de disco: usa a lista da própria imagem.
  const cdExtension = ['cue', 'chd', 'iso', 'ccd', 'mds', 'toc']
    .find((ext) => extensions.has(ext));
  if (cdExtension) {
    return {
      candidates: LOCAL_AMBIGUOUS_EXTENSIONS[cdExtension] || LOCAL_AMBIGUOUS_EXTENSIONS.cue,
      message: `O .zip contém um dump de CD (.${cdExtension}). Selecione o sistema de CD correspondente:`,
    };
  }

  // Extensões inequívocas encontradas lá dentro.
  const resolvedCores = new Set();
  const ambiguousLists = [];
  extensions.forEach((ext) => {
    const mapped = LOCAL_EXTENSION_CORE_MAP[ext];
    if (mapped) resolvedCores.add(mapped);
    else if (LOCAL_AMBIGUOUS_EXTENSIONS[ext]) ambiguousLists.push(LOCAL_AMBIGUOUS_EXTENSIONS[ext]);
  });

  // Um único sistema reconhecido → carrega direto, sem perguntar.
  if (resolvedCores.size === 1) {
    return { core: [...resolvedCores][0], reason: 'ROM reconhecida no conteúdo' };
  }

  // Vários sistemas reconhecidos (zip misto) → seletor só com eles.
  if (resolvedCores.size > 1) {
    return {
      candidates: [...resolvedCores],
      message: 'O .zip contém ROMs de mais de um sistema. Qual deseja jogar?',
    };
  }

  // Nada reconhecido, MUITOS arquivos e extensões fora de qualquer mapa
  // (ex.: epr-12340.ic1): assinatura típica de romset arcade (MAME/FBNeo),
  // onde o zip inteiro é a "ROM". Romsets reais às vezes misturam .rom e
  // afins, então Arcade vem primeiro e as opções ambíguas entram depois.
  // Um único arquivo desconhecido NÃO cai aqui — vai para o seletor padrão.
  const unrecognized = [...extensions].filter(
    (ext) => !LOCAL_EXTENSION_CORE_MAP[ext] && !LOCAL_AMBIGUOUS_EXTENSIONS[ext]
  );
  if (entries.length >= 4 && unrecognized.length > 0) {
    const union = [...new Set(ambiguousLists.flat())]
      .filter((core) => core !== 'arcade' && core !== 'mame2003');
    return {
      candidates: ['arcade', 'mame2003', ...union],
      message: 'O conteúdo parece um romset de arcade. Escolha o core:',
    };
  }

  // Só extensões ambíguas (ex.: um único .bin) → união das listas delas.
  if (ambiguousLists.length) {
    const union = [...new Set(ambiguousLists.flat())];
    return {
      candidates: union,
      message: 'Sistemas compatíveis com o conteúdo do .zip:',
    };
  }

  return null;
}

function getLocalGameTitle(fileName) {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
}

/**
 * Abre o seletor de sistema e resolve com o core escolhido (ou null se
 * cancelado). Usado quando a extensão é ambígua ou desconhecida.
 */
function openCorePicker(fileName, extension, candidates, customMessage) {
  return new Promise((resolve) => {
    state.corePickerResolve = resolve;
    state.corePickerLastFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    dom.corePickerText.textContent = customMessage
      ? `Arquivo "${fileName}" — ${customMessage}`
      : `O arquivo "${fileName}" (.${extension}) pode pertencer a mais de um sistema. Selecione o core correto:`;

    dom.corePickerGrid.innerHTML = '';
    candidates.forEach((core) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn-soft core-picker-option';
      button.textContent = LOCAL_CORE_LABELS[core] || core;
      button.addEventListener('click', () => {
        const resolveFn = state.corePickerResolve;
        state.corePickerResolve = null;
        dom.corePicker.classList.add('is-hidden');
        layerClosed('core-picker');
        if (resolveFn) resolveFn(core);
      });
      dom.corePickerGrid.appendChild(button);
    });

    dom.corePicker.classList.remove('is-hidden');
    layerOpened('core-picker');
    // Realce/foco na primeira opção: o diálogo nasce navegável por teclado
    // (Tab/setas + Esc) E por controle (d-pad move, A confirma, B cancela).
    gpPickerHighlight(0);
  });
}

function restoreCorePickerFocus() {
  const previous = state.corePickerLastFocus;
  state.corePickerLastFocus = null;
  if (previous && document.contains(previous)) {
    try { previous.focus(); } catch (_) { /* noop */ }
  }
}

function closeCorePicker() {
  const resolveFn = state.corePickerResolve;
  state.corePickerResolve = null;
  state.gpPickerIndex = -1;
  dom.corePicker.classList.add('is-hidden');
  layerClosed('core-picker');
  if (resolveFn) resolveFn(null);
}

// @generated-exports
export { DOCUMENTATION_BASENAME, ZIP_META_EXTENSIONS, analyzeZipRom, closeCorePicker, getLocalGameTitle, isDocumentationMarkdown, listZipEntries, openCorePicker, restoreCorePickerFocus };
// @generated-exports
